/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
/**
 * Verifies if the required role is covered by the assigned roles.
 */
export const hasOrganizationPermission = (
  roles: OrganizationRole[],
  requiredPermission: OrganizationPermission,
): boolean => {
  return roles.some(role => OrganizationRolePermissions[role][requiredPermission]);
};

export const OrganizationRoles = ['owner', 'project-creator', 'member', 'read-only'] as const;
export type OrganizationRole = (typeof OrganizationRoles)[number];

/**
 * All these permissions are within an organization, not it's projects
 */
export const OrganizationPermissions = [
  /**
   * Read information about the organization
   */
  'read_organization',
  /**
   * Administrating the organization
   * - changing the organization details and address
   * - changing the organization thumbnail
   * - inviting and removing users to the organization
   * - changing the role of organization users
   * - sending confirmation emails
   */
  'edit_organization',
  /**
   * Delete the organization
   */
  'delete_organization',

  /**
   * Create a new project within the organization
   */
  'create_project',

  /**
   * Create a new team within the organization
   */
  'create_team',
  /**
   * Read all teams of the organization, including their members
   */
  'read_team',
  /**
   * Administrating a team:
   *  - changing the name, description and role
   *  - adding and removing members
   */
  'edit_team',
  /**
   * Delete the team
   */
  'delete_team',
] as const;
export type OrganizationPermission = (typeof OrganizationPermissions)[number];

const OrganizationRolePermissions: Record<OrganizationRole, Record<OrganizationPermission, boolean>> = {
  member: {
    create_project: false,
    create_team: false,
    delete_organization: false,
    delete_team: false,
    edit_organization: false,
    edit_team: false,
    read_organization: true,
    read_team: true,
  },
  owner: {
    create_project: true,
    create_team: true,
    delete_organization: true,
    delete_team: true,
    edit_organization: true,
    edit_team: true,
    read_organization: true,
    read_team: true,
  },
  'project-creator': {
    create_project: true,
    create_team: false,
    delete_organization: false,
    delete_team: false,
    edit_organization: false,
    edit_team: false,
    read_organization: true,
    read_team: true,
  },
  'read-only': {
    create_project: false,
    create_team: false,
    delete_organization: false,
    delete_team: false,
    edit_organization: false,
    edit_team: false,
    read_organization: true,
    read_team: true,
  },
};
